import { useContext } from "react";
import type { ReactNode, ComponentType } from "react";
import type { StatEntry } from "./Types/StatItemType";
import { TabRegistryContext } from "./Internal/TabRegistryContext";
import { PanelContext } from "./PanelContext";
import { EmptyState } from "../Display/EmptyState";
import { ControlBar } from "./ControlBar";
import {
  ControlBarLabelProvider,
  type ControlBarLabelPosition,
} from "./Internal/ControlBarLabelContext";
import { TabStats_ } from "./Internal/TabStats";
import { TabHeaderStats_ } from "./Internal/TabHeaderStats";

export type TabCountColor =
  | "gold"
  | "green"
  | "red"
  | "blue"
  | "orange"
  | "purple"
  | "grey";

export interface TabWarning {
  /** Tooltip content shown when hovering the warning indicator. */
  tooltip: ReactNode;
}

export interface TabProps {
  id: string;
  title: string;
  /**
   * Forwarded to the tab header trigger for test selection. The count badge
   * gets `<testId>-count`, since the title and the count are separate elements
   * and a test can't match them as one string.
   */
  testId?: string | undefined;
  count?: number | undefined;
  /** Override the count badge color. Defaults to blue when active. */
  countColor?: TabCountColor | undefined;
  /** Disable the tab — greyed out, not clickable, skipped during active-tab resolution. */
  disabled?: boolean | undefined;
  icon?: ReactNode;
  /**
   * Adds a gold warning indicator (with hover tooltip) before the tab
   * label so ops can see at a glance which tabs need attention.
   */
  warning?: TabWarning | undefined;
  /** Stat items shown at the top of the tab content area. */
  statsBar?: StatEntry[] | undefined;
  /** Compact stats shown inline in the tab header, to the left of controls. */
  leftStats?: StatEntry[] | undefined;
  /**
   * Keep `leftStats` on the same row as the right-side controls instead of
   * splitting them onto a dedicated row above. By default, when leftStats and
   * right-side controls both exist (and there are no leftFilters), they're
   * stacked for scannability; set this when the stats are short enough to share
   * the controls row.
   */
  inlineStats?: boolean | undefined;
  /** Left-aligned filter controls in the tab header (same row as filters/actions/menu) */
  leftFilters?: ReactNode;
  /** Compact stats shown inline in the tab header, to the right of controls. */
  rightStats?: StatEntry[] | undefined;
  /** Right-aligned labeled filter controls in the tab header */
  rightFilters?: ReactNode;
  /**
   * Label placement for every ControlBarItem in this tab's filter slots
   * (leftFilters, rightFilters, filterBar). Set once here so the whole row
   * is consistent. Defaults to "beside".
   */
  filtersLabelPosition?: ControlBarLabelPosition | undefined;
  /** Right-aligned action buttons in the tab header */
  rightActions?: ReactNode;
  /**
   * Route the right action buttons through the overflow ActionBar: the ones
   * that fit show with their full label, the rest fold into the kebab. Either
   * value works; omit for a run of actions that should never overflow.
   */
  actionCollapse?: "always" | "responsive" | undefined;
  /** Menu items — auto-wrapped in a Menu kebab. Pass Button children directly. */
  menu?: ReactNode;
  /** Left-aligned action buttons in the tab header controls row. */
  leftActions?: ReactNode;
  /** Full-width filter/control bar below the controls row, separated by a border. */
  filterBar?: ReactNode;
  /** Description shown below the tab content title */
  description?: ReactNode;
  /** Banners shown between the header and content. Always padded, even in fullBleed mode. */
  banners?: ReactNode | undefined;
  /** When true, content renders edge-to-edge (no padding). Use for tables. */
  fullBleed?: boolean | undefined;
  /** Phosphor icon component shown in the EmptyState when fullBleed tab has count 0. */
  emptyIcon?: ComponentType<{ className?: string }> | undefined;
  /** Empty-state title when fullBleed tab has count 0. Defaults to "No {title}". */
  emptyTitle?: string | undefined;
  /** Empty-state description when fullBleed tab has count 0 (e.g. point the user elsewhere). */
  emptyDescription?: string | undefined;
  children: ReactNode;
}

/** Declares a tab inside `<Tabs>`. Props are read by Tabs to build headers. */
// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function Tab({
  id,
  title,
  testId,
  children,
  count,
  countColor,
  disabled,
  icon,
  warning,
  statsBar,
  leftStats,
  inlineStats,
  leftFilters,
  rightStats,
  rightFilters,
  rightActions,
  actionCollapse,
  menu,
  leftActions,
  filterBar,
  filtersLabelPosition,
  description,
  banners,
  fullBleed,
  emptyIcon,
  emptyTitle,
  emptyDescription,
}: TabProps) {
  const registry = useContext(TabRegistryContext);

  // When inside a registry-based Tabs, register metadata and
  // conditionally render based on active tab.
  if (registry) {
    const list = registry.metaRef.current;
    if (!list.some((m) => m.id === id)) {
      list.push({
        id,
        title,
        testId,
        count,
        countColor,
        disabled,
        icon,
        warning,
        statsBar,
        leftStats,
        leftFilters,
        rightStats,
        rightFilters,
        rightActions,
        menu,
        leftActions,
        filterBar,
        filtersLabelPosition,
        description,
        fullBleed,
        emptyIcon,
      });
    }

    if (registry.activeId !== id) {
      return <></>;
    }

    // When the tab header would carry ONLY the kebab / right actions (no filters
    // or left content) and there's a statsBar, render those controls on the
    // statsBar row instead of leaving an otherwise-empty header band above it.
    const headerHasOnlyRightControls =
      !!(menu || rightActions) &&
      !(leftStats && leftStats.length > 0) &&
      !leftFilters &&
      !leftActions &&
      !rightFilters &&
      !(rightStats && rightStats.length > 0);
    const mergeControlsIntoStatsRow =
      !fullBleed &&
      !!(statsBar && statsBar.length > 0) &&
      headerHasOnlyRightControls;

    // A tab has no in-content title (the title lives in the tab strip), so a
    // description otherwise lands on its own row *below* the right-aligned
    // action row, leaving an empty band above it. When there's no left-hand
    // content, seat the description on the left of that row instead — mirroring
    // Page/Panel headers (description left, actions right).
    const descriptionInControlsRow =
      !!description &&
      !fullBleed &&
      !mergeControlsIntoStatsRow &&
      !(leftFilters || leftActions || (leftStats && leftStats.length > 0)) &&
      !!(
        rightFilters ||
        rightActions ||
        menu ||
        (rightStats && rightStats.length > 0)
      );

    // Active tab in registry mode: render the full content panel.
    return (
      <div className={`mt-4 ${fullBleed ? "" : "flex flex-col gap-4"}`}>
        {(leftStats ||
          leftFilters ||
          leftActions ||
          rightFilters ||
          rightStats ||
          rightActions ||
          menu) &&
          !fullBleed &&
          !mergeControlsIntoStatsRow &&
          // oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
          (() => {
            const hasLeftStats = !!(leftStats && leftStats.length > 0);
            const hasOtherLeft = !!(leftFilters || leftActions);
            const hasRightControls = !!(
              rightFilters ||
              rightActions ||
              menu ||
              (rightStats && rightStats.length > 0)
            );
            const labelPosition = filtersLabelPosition ?? "beside";
            // See TabPanel_: top-align when labels stack above controls so the
            // labels line up; default stays items-center (byte-identical markup).
            const rowAlign =
              labelPosition === "above" ? "items-start" : "items-center";

            // When the leftStats summary AND right-side controls both exist,
            // their content is dense enough that crowding them onto a single
            // row hurts scannability (e.g. the Pricing tab's LIVE/REQUESTED/
            // DRAFT counts colliding with carrier/status/expiring filters).
            // Split them onto two stacked rows in that case so each gets the
            // full width of the panel. Single-purpose tabs (only stats, only
            // filters) keep the original single-row layout.
            const splitStatsFromControls =
              hasLeftStats && hasRightControls && !hasOtherLeft && !inlineStats;

            if (splitStatsFromControls) {
              return (
                <>
                  <div className="mb-3 flex items-center">
                    <TabHeaderStats_ items={leftStats!} showDivider={false} />
                  </div>
                  <div className="mb-4 flex items-center justify-end">
                    {/* flex-1 so the overflow ActionBar has a real width to
                        measure its fold against. */}
                    <div className="flex min-w-0 flex-1 items-center justify-end gap-5">
                      {rightStats && rightStats.length > 0 && (
                        <TabHeaderStats_
                          items={rightStats}
                          showDivider={!!(rightFilters || rightActions || menu)}
                        />
                      )}
                      <ControlBar
                        filters={rightFilters}
                        actions={rightActions}
                        actionCollapse={actionCollapse}
                        menu={menu}
                        labelPosition={labelPosition}
                      />
                    </div>
                  </div>
                </>
              );
            }

            return (
              <div
                className={`mb-4 flex flex-wrap ${rowAlign} gap-y-2 ${leftFilters || leftActions || leftStats || descriptionInControlsRow ? "justify-between" : "justify-end"}`}
              >
                <div className={`flex flex-wrap ${rowAlign} gap-x-5 gap-y-2`}>
                  {descriptionInControlsRow && (
                    <div className="min-w-0">{description}</div>
                  )}
                  {leftFilters && (
                    <ControlBarLabelProvider value={labelPosition}>
                      <div
                        className={`flex flex-wrap ${rowAlign} gap-x-3 gap-y-2`}
                      >
                        {leftFilters}
                      </div>
                    </ControlBarLabelProvider>
                  )}
                  {leftActions && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                      {leftActions}
                    </div>
                  )}
                  {leftStats && leftStats.length > 0 && (
                    <TabHeaderStats_
                      items={leftStats}
                      showDivider={!!(leftFilters || leftActions)}
                    />
                  )}
                </div>
                {/* flex-1 + justify-end so the action cluster spans to the
                    right edge and the overflow ActionBar has a real width to
                    measure its fold against. */}
                <div
                  className={`flex min-w-0 flex-1 ${rowAlign} justify-end gap-5`}
                >
                  {rightStats && rightStats.length > 0 && (
                    <TabHeaderStats_
                      items={rightStats}
                      showDivider={!!(rightFilters || rightActions || menu)}
                    />
                  )}
                  <ControlBar
                    filters={rightFilters}
                    actions={rightActions}
                    actionCollapse={actionCollapse}
                    menu={menu}
                    labelPosition={labelPosition}
                  />
                </div>
              </div>
            );
          })()}
        {filterBar && !fullBleed && (
          <div className="mb-4 border-t border-border pt-3">
            <ControlBar
              filters={filterBar}
              labelPosition={filtersLabelPosition ?? "beside"}
            />
          </div>
        )}
        {statsBar &&
          statsBar.length > 0 &&
          (mergeControlsIntoStatsRow ? (
            <div className="mb-4 flex flex-wrap items-start justify-between gap-y-2">
              <TabStats_ items={statsBar} />
              {/* flex-1 so the overflow ActionBar has a real width to measure
                  its fold against (keeps actions on the stats row). */}
              <div className="flex min-w-0 flex-1 justify-end">
                <ControlBar
                  filters={rightFilters}
                  actions={rightActions}
                  actionCollapse={actionCollapse}
                  menu={menu}
                  labelPosition={filtersLabelPosition ?? "beside"}
                />
              </div>
            </div>
          ) : (
            <div className={fullBleed ? "px-5 pt-4" : "mb-4"}>
              <TabStats_ items={statsBar} />
            </div>
          ))}
        {description && !descriptionInControlsRow && (
          <div className={fullBleed ? "px-5 pt-5" : "mb-4"}>{description}</div>
        )}
        {banners && (
          <div
            className={
              fullBleed
                ? "flex flex-col gap-2 px-5 pt-4 pb-2"
                : "mb-4 flex flex-col gap-2"
            }
          >
            {banners}
          </div>
        )}
        {fullBleed && count === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={emptyIcon}
              title={emptyTitle ?? `No ${title}`}
              description={emptyDescription}
            />
          </div>
        ) : fullBleed ? (
          <PanelContext.Provider value={true}>{children}</PanelContext.Provider>
        ) : (
          children
        )}
      </div>
    );
  }

  return <>{children}</>;
}
